import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Btn, DataTable, Loading, Msg, Section, TextInput, panelGet, panelPost } from '../../kit';
import { Badge } from '../emailKit';
import { AdminLink, type AdminTarget } from '../../adminNav';

/**
 * Delivery trace (backend: lookup/trace) — owner request 2026-10-05, after a
 * sign-in code bounced, was filed 22 h later, and nobody could tell what the
 * bounced mail had been or whether the person signed up another way.
 *
 * One address (or a pasted SendGrid message id / conversation id) and
 * everything about the mail we sent it: what each e-mail was, SendGrid's
 * events in order, the incidents filed and how late, the sign-in codes asked
 * for, the account if there is one, and accounts that may be the same person.
 *
 * Deep link: /admin?group=email&tab=trace&email=…[&msg=<sg id>][&incident=<id>]
 * — the message (or the incident's message) is expanded, highlighted and
 * scrolled to.
 */

export type TracePreset = { email?: string; msg?: string; incident?: string; nonce: string };
type Query = { email?: string; msg?: string; incident?: string };
type Note = { kind: 'error' | 'ok'; text: string } | null;

/** IST, like the rest of the admin; `secs` for event-level detail. */
export const fmtIST = (v: any, secs = false): string => {
  if (v === null || v === undefined || v === '') return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    ...(secs ? { second: '2-digit' as const } : {}), timeZone: 'Asia/Kolkata',
  });
};

const fmtShort = (v: any): string => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v ?? '—') : d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
};

/** How long after the failure it reached us: "within a minute", "22 h later". */
export const lagText = (s: any): string => {
  const n = Number(s);
  if (s === null || s === undefined || s === '' || !Number.isFinite(n)) return '';
  if (n < 60) return 'within a minute';
  const m = Math.round(n / 60);
  if (m < 60) return `${m} min later`;
  const h = Math.round(n / 3600);
  return h < 48 ? `${h} h later` : `${Math.round(n / 86400)} d later`;
};

/** "sync · 22 h later" — how an incident was filed (webhook or the daily sync) and how late. */
export const filedText = (source: any, filedAfterS: any): string =>
  [source ? String(source) : '', lagText(filedAfterS)].filter(Boolean).join(' · ');

const minsText = (mins: any): string => {
  const n = Number(mins);
  if (!Number.isFinite(n)) return '';
  if (n < 120) return `${Math.round(n)} min`;
  return n < 48 * 60 ? `${Math.round(n / 60)} h` : `${Math.round(n / 1440)} d`;
};

const STATUS_TONE: Record<string, 'green' | 'red' | 'amber' | 'grey' | 'blue'> = {
  delivered: 'green', bounced: 'red', blocked: 'red', dropped: 'red', failed: 'red',
  deferred: 'amber', processed: 'grey', sent: 'blue',
};
const CONF_TONE: Record<string, 'green' | 'amber' | 'grey'> = { likely: 'green', possible: 'amber', weak: 'grey' };
const PENDING_LABEL: Record<string, string> = { approval: 'Approval request', invite_request: 'Invite request' };
const dot = (type: string) => {
  const t = String(type || '').toLowerCase();
  if (/bounce|block|drop|fail|spam/.test(t)) return 'bg-red-600';
  if (t.includes('defer')) return 'bg-amber-500';
  if (t.includes('deliver')) return 'bg-green-600';
  if (/open|click/.test(t)) return 'bg-emerald-600';
  return 'bg-grayscale-600';
};
const safeUrl = (u: any) => (typeof u === 'string' && /^https?:\/\//i.test(u.trim()) ? u.trim() : '');
const byTime = (dir: 1 | -1) => (a: any, b: any) =>
  ((Date.parse(a?.at) || 0) - (Date.parse(b?.at) || 0)) * dir;

/** The typed box → what to trace. A pasted id goes through the global search,
 *  which knows conversation ids and SendGrid ids and which address they were for. */
const resolveInput = async (raw: string): Promise<Query | null> => {
  const v = raw.trim().replace(/^<|>$/g, '').trim();
  if (!v) return null;
  if (v.includes('@')) return { email: v.toLowerCase() };
  const inc = v.match(/^(?:incident\s*#?|#)(\d+)$/i);
  if (inc) return { incident: inc[1] };
  const { data } = await panelGet('lookup', 'search', { q: v, limit: 6 });
  for (const g of data?.groups || []) {
    for (const it of g?.items || []) {
      const l = it?.link;
      if (l?.group === 'email' && l?.tab === 'trace' && (l.email || l.msg || l.incident)) {
        return { email: l.email || undefined, msg: l.msg || undefined, incident: l.incident ? String(l.incident) : undefined };
      }
    }
  }
  return { msg: v };
};

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="min-w-0"><div className="text-grayscale-1100">{label}</div><div className="text-grayscale-1300 truncate">{children}</div></div>
);

/** What the e-mail was — the thing people ask first, so it leads each row. */
const SourcePill: React.FC<{ src: any }> = ({ src }) => {
  const label = String(src?.label || 'E-mail');
  const cls = 'inline-flex items-center rounded-full bg-primary-100 text-primary-700 border border-primary-300 '
    + 'px-2.5 py-0.5 text-xs font-bold font-grotesk whitespace-nowrap';
  return src?.link?.group
    ? <AdminLink to={src.link as AdminTarget} className={`${cls} hover:bg-primary-200`} title={src.detail || 'Open it'}>{label} ↗</AdminLink>
    : <span className={cls} title={src?.detail || undefined}>{label}</span>;
};

const LIMIT = 15;

const DeliveryTrace: React.FC<{ preset?: TracePreset | null }> = ({ preset }) => {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState<Query | null>(null);
  const [res, setRes] = useState<{ data: any; loading: boolean; error: string }>({ data: null, loading: false, error: '' });
  const [note, setNote] = useState<Note>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);
  const [scrollTo, setScrollTo] = useState<{ key: string; n: number } | null>(null);
  const [resolving, setResolving] = useState('');
  const seq = useRef(0);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Newest request wins; `keep` (a refresh) leaves what is expanded alone.
  const run = useCallback(async (q: Query, keep = false) => {
    const mine = ++seq.current;
    setQuery(q);
    setRes(r => ({ ...r, loading: true, error: '' }));
    const params: Record<string, string> = {};
    (['email', 'msg', 'incident'] as const).forEach(k => { if (q[k]) params[k] = String(q[k]); });
    const { data, error } = await panelGet('lookup', 'trace', params);
    if (mine !== seq.current) return;
    setRes({ data, loading: false, error });
    if (!keep && data) {
      const k = String(data.focus?.message_key || '');
      setOpen(new Set(k ? [k] : []));
      setShowAll(false);
      if (k) setScrollTo({ key: k, n: mine });
    }
  }, []);

  const go = async () => {
    const mine = ++seq.current;
    setNote(null);
    setRes(r => ({ ...r, loading: true, error: '' }));
    const q = await resolveInput(input);
    if (mine !== seq.current) return;
    if (!q) {
      setRes(r => ({ ...r, loading: false }));
      setNote({ kind: 'error', text: 'Type an e-mail address, or paste a SendGrid message id or conversation id.' });
      return;
    }
    run(q);
  };

  useEffect(() => {
    if (!preset) return;
    const q: Query = {};
    if (preset.email) q.email = preset.email;
    if (preset.msg) q.msg = preset.msg;
    if (preset.incident) q.incident = preset.incident;
    if (!q.email && !q.msg && !q.incident) return;
    setNote(null);
    setInput(q.email || q.msg || `incident ${q.incident}`);
    run(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset?.nonce]);

  useEffect(() => {
    if (!scrollTo) return;
    const t = setTimeout(() => rowRefs.current[scrollTo.key]?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
    return () => clearTimeout(t);
  }, [scrollTo]);

  const d = res.data;
  const focusKey = String(d?.focus?.message_key || '');
  const focusInc = String(d?.focus?.incident_id ?? '');
  const messages: any[] = useMemo(() => [...(d?.messages || [])].sort(byTime(-1)), [d]);
  const msgKeys = useMemo(() => new Set(messages.map(m => String(m.key))), [messages]);
  const shown = showAll ? messages : messages.filter((m, i) => i < LIMIT || open.has(String(m.key)));
  const incidents: any[] = useMemo(() => (d?.incidents || []).map((i: any) => ({
    ...i, filed: filedText(i.source, i.filed_after_s),
  })), [d]);
  const otp: any[] = d?.otp || [];
  const related: any[] = d?.related || [];
  const pending: any[] = d?.pending || [];
  const acc = d?.account;

  const toggle = (k: string) => setOpen(s => {
    const n = new Set(s);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  const showMsg = (k: string) => {
    setOpen(s => new Set(s).add(k));
    setScrollTo({ key: k, n: Date.now() });
  };
  const resolve = async (id: any) => {
    setResolving(String(id)); setNote(null);
    const { data, error } = await panelPost('email', 'health_resolve', { id });
    setResolving('');
    if (error || data?.success === false) {
      setNote({ kind: 'error', text: error || data?.error || 'Could not mark it resolved' });
      return;
    }
    if (query) run(query, true);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        {/* Enter or the button submits; Refresh stays outside so it never re-runs the search. */}
        <form className="flex items-center gap-2 flex-1 min-w-[260px]" onSubmit={e => { e.preventDefault(); go(); }}>
          <TextInput value={input} onChange={setInput} wide
            placeholder="E-mail address, SendGrid message id or conversation id…" />
          <Btn kind="primary" disabled={res.loading}>{res.loading ? 'Tracing…' : 'Trace'}</Btn>
        </form>
        {query && d && <Btn small onClick={() => run(query, true)}>Refresh</Btn>}
        {typeof d?.took_ms === 'number' && <span className="text-xs text-grayscale-1100">traced in {d.took_ms} ms</span>}
      </div>
      {note && <Msg kind={note.kind}>{note.text}</Msg>}
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {!d && !res.loading && !res.error && (
        <p className="text-sm text-grayscale-1100">
          Every e-mail we sent an address, what each one was (sign-in code, campaign, welcome …), what
          SendGrid saw happen to it, the failures filed and how late, and whether the person has an account.
        </p>
      )}
      {res.loading && !d && <Loading label="Tracing…" />}

      {d && (
        <div className={`flex flex-col gap-4 ${res.loading ? 'opacity-60' : ''}`}>
          {d.summary && (
            <div className="bg-white rounded-xl border border-grayscale-200 px-4 py-3 text-sm text-grayscale-1300 font-medium">
              {d.summary}
            </div>
          )}
          {d.moved_to && (
            <div className="text-sm rounded-lg border px-3 py-2 text-amber-800 bg-amber-50 border-amber-200
              flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>
                This address was retired on {fmtIST(d.moved_to.at)} IST: the account moved to{' '}
                <b className="break-all">{d.moved_to.new_email || '—'}</b>
                {d.moved_to.by ? <span className="text-amber-700"> (changed by {d.moved_to.by})</span> : null}
              </span>
              {d.moved_to.link?.group && (
                <AdminLink to={d.moved_to.link as AdminTarget} title="Open the account">Open the account</AdminLink>
              )}
              {d.moved_to.new_email && (
                <AdminLink to={{ group: 'email', tab: 'trace', email: String(d.moved_to.new_email) }}>
                  Trace new address
                </AdminLink>
              )}
            </div>
          )}
          {d.suppressed && (
            <div className="text-sm rounded-lg border px-3 py-2 text-amber-800 bg-amber-50 border-amber-200">
              <b>Unresolved {d.suppressed.kind || 'failure'}</b> since {fmtIST(d.suppressed.since)} IST. SendGrid may
              be dropping later mail to this address (sign-in codes included) without trying to deliver it.
            </div>
          )}
          {(d.warnings || []).map((w: string, i: number) => <Msg key={i} kind="error">{w}</Msg>)}

          <Section title={d.email ? `Account — ${d.email}` : 'Account'}
            right={<div className="flex gap-3 text-xs">
              {acc && <AdminLink to={acc.link?.group ? acc.link : { group: 'user360', email: acc.email }}>Open in User 360</AdminLink>}
              {d.email && <AdminLink to={{ group: 'email', tab: 'people', email: d.email }}>People timeline</AdminLink>}
            </div>}>
            {acc ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <Fact label="Name">{acc.name || '—'}</Fact>
                  <Fact label="Account created (IST)">{fmtIST(acc.created_at)}</Fact>
                  <Fact label="How they came">{acc.origin_label || acc.origin || '—'}</Fact>
                  <Fact label="Last signed in (IST)">{acc.last_login_at ? fmtIST(acc.last_login_at) : 'never'}</Fact>
                </div>
                {(acc.previous_emails || []).length > 0 && (
                  <div className="text-xs text-grayscale-1200 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-grayscale-1100">Previous sign-in addresses:</span>
                    {(acc.previous_emails as string[]).map(e => (
                      <AdminLink key={e} to={{ group: 'email', tab: 'trace', email: e }}
                        title="Trace this address — its e-mail history stays under it">{e}</AdminLink>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-grayscale-1200">No account for this address.</div>
            )}
            {pending.length > 0 && (
              <ul className="text-xs text-grayscale-1200 flex flex-col gap-0.5">
                {pending.map((p, i) => (
                  <li key={i}>
                    <b>{PENDING_LABEL[p.kind] || p.kind}</b> · {fmtIST(p.created_at)} IST · {p.status || '—'}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title={`Incidents (${incidents.length})`}>
            <DataTable rows={incidents} maxHeight="20rem"
              columns={['occurred_at', 'kind', 'reason', 'filed', 'notified_at']}
              labels={{ occurred_at: 'When (IST)', filed: 'Filed', notified_at: 'Notified / resolved' }}
              render={{
                occurred_at: r => (
                  <span className={String(r.id) === focusInc ? 'font-bold text-amber-800' : ''}>
                    {String(r.id) === focusInc ? '▶ ' : ''}{fmtIST(r.occurred_at)}
                  </span>
                ),
                kind: r => <Badge tone={r.kind === 'deferred' ? 'amber' : 'red'}>{r.kind || '—'}</Badge>,
                reason: r => <span className="block w-[12rem] whitespace-normal break-words text-xs">{r.reason || '—'}</span>,
                filed: r => (
                  <span className={Number(r.filed_after_s) >= 3600 ? 'text-amber-700' : ''}
                    title={r.recorded_at ? `Filed ${fmtIST(r.recorded_at)} IST` : undefined}>{r.filed || '—'}</span>
                ),
                notified_at: r => (
                  <div className="text-xs leading-snug">
                    <div>{r.notified_at ? `notified ${fmtShort(r.notified_at)}` : 'not notified yet'}</div>
                    <div className={r.resolved_at ? 'text-green-800' : 'text-grayscale-1100'}>
                      {r.resolved_at ? `resolved ${fmtShort(r.resolved_at)}` : 'open'}
                    </div>
                  </div>
                ),
              }}
              actions={r => (
                <div className="flex flex-col gap-1 items-end">
                  {r.message_key && msgKeys.has(String(r.message_key)) && (
                    <Btn small onClick={() => showMsg(String(r.message_key))}>E-mail</Btn>
                  )}
                  {!r.resolved_at && (
                    <Btn small disabled={resolving === String(r.id)} onClick={() => resolve(r.id)}
                      title="Mark it dealt with. SendGrid's own suppression list is not changed.">
                      {resolving === String(r.id) ? 'Resolving…' : 'Resolve'}
                    </Btn>
                  )}
                </div>
              )}
              empty="No delivery failures filed for this address." />
          </Section>

          <Section title={`E-mails sent to this address (${messages.length})`}
            right={messages.length > LIMIT
              ? <Btn small onClick={() => setShowAll(v => !v)}>{showAll ? `Show the latest ${LIMIT}` : `Show all ${messages.length}`}</Btn>
              : undefined}>
            {!messages.length && <div className="text-sm text-grayscale-1100 py-2">No e-mail to this address on record.</div>}
            <div className="flex flex-col divide-y divide-grayscale-200">
              {shown.map(m => {
                const k = String(m.key);
                const isOpen = open.has(k);
                const events: any[] = [...(m.events || [])].sort(byTime(1));
                return (
                  <div key={k} ref={el => { rowRefs.current[k] = el; }}
                    className={`py-2 px-2 -mx-2 rounded-lg ${k === focusKey ? 'bg-amber-50 ring-1 ring-amber-300' : ''}`}>
                    <div className="flex items-start gap-2">
                      <button type="button" onClick={() => toggle(k)} aria-expanded={isOpen}
                        title={isOpen ? 'Hide the events' : 'Show the events'}
                        className="w-4 pt-0.5 text-xs text-grayscale-1100">{isOpen ? '▾' : '▸'}</button>
                      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <SourcePill src={m.source} />
                          <button type="button" onClick={() => toggle(k)}
                            className="text-sm text-grayscale-1300 font-medium text-left truncate max-w-full hover:underline">
                            {m.subject || '(no subject)'}
                          </button>
                          <span className="ml-auto inline-flex gap-1">
                            <Badge tone={STATUS_TONE[m.status] || 'grey'}>{m.status || 'unknown'}</Badge>
                            {m.opened && <Badge tone="green">opened</Badge>}
                            {m.clicked && <Badge tone="green">clicked</Badge>}
                          </span>
                        </div>
                        <div className="text-[11px] text-grayscale-1100 break-words">
                          {fmtIST(m.at)} IST{m.source?.detail ? ` · ${m.source.detail}` : ''}
                          {m.reason && <span className="text-red-700"> · {m.reason}</span>}
                        </div>
                      </div>
                    </div>
                    {isOpen && (
                      <div className="ml-6 mt-1.5 flex flex-col gap-1">
                        {!events.length && <div className="text-xs text-grayscale-1100">No SendGrid events recorded for it.</div>}
                        {events.length > 0 && (
                          <ol className="border-l border-grayscale-200 ml-1">
                            {events.map((e, i) => {
                              const url = safeUrl(e.url);
                              return (
                                <li key={i} className="relative pl-4 py-0.5 text-xs">
                                  <span aria-hidden className={`absolute -left-[4.5px] top-1.5 w-2 h-2 rounded-full ${dot(e.type)}`} />
                                  <span className="text-grayscale-1100 mr-2 tabular-nums">{fmtIST(e.at, true)}</span>
                                  <span className="text-grayscale-1300">{e.label || e.type}</span>
                                  {e.reason && <div className="text-red-700 break-words">{e.reason}</div>}
                                  {url && (
                                    <div className="truncate">
                                      <a href={url} target="_blank" rel="noopener noreferrer" className="underline text-grayscale-1200">{e.url}</a>
                                    </div>
                                  )}
                                </li>
                              );
                            })}
                          </ol>
                        )}
                        <div className="text-[11px] text-grayscale-1100 break-all">
                          {[m.purpose && `purpose ${m.purpose}`, m.conversation_id && `conversation ${m.conversation_id}`,
                            `id ${k}`].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Section>

          {otp.length > 0 && (
            <Section title={`Sign-in codes requested (${otp.length})`}>
              <DataTable rows={otp} csvName="trace_otp" maxHeight="16rem"
                columns={['created_at', ...(otp.some(o => o.purpose) ? ['purpose'] : []), 'attempts', 'consumed', 'expires_at',
                  ...(otp.some(o => o.visitor_id) ? ['visitor_id'] : [])]}
                labels={{ created_at: 'Requested (IST)', consumed: 'Used', expires_at: 'Expires (IST)', visitor_id: 'Visitor' }}
                render={{
                  created_at: r => fmtIST(r.created_at),
                  expires_at: r => fmtIST(r.expires_at),
                  consumed: r => (r.consumed ? <Badge tone="green">used</Badge> : <span className="text-grayscale-1100">not used</span>),
                }} />
            </Section>
          )}

          {related.length > 0 && (
            <Section title={`Possibly the same person (${related.length})`}>
              <p className="text-xs text-grayscale-1100 -mt-1">
                A heuristic, not a match: accounts that appeared around the same time and share a signal with this
                address. Check before acting on it.
              </p>
              <div className="flex flex-col divide-y divide-grayscale-200">
                {related.map((r, i) => (
                  <div key={r.user_id || i} className="py-1.5 flex flex-col gap-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge tone={CONF_TONE[r.confidence] || 'grey'}>{r.confidence || '—'}</Badge>
                      <AdminLink to={r.link?.group ? r.link : { group: 'user360', email: r.email }}
                        className="text-sm text-primary-700 underline underline-offset-2" title="Open in User 360">{r.email}</AdminLink>
                      {r.name && <span className="text-sm text-grayscale-1200">{r.name}</span>}
                      <span className="ml-auto text-[11px] text-grayscale-1100">
                        created {fmtIST(r.created_at)} IST
                        {minsText(r.minutes_after) ? ` · ${minsText(r.minutes_after)} after` : ''}
                      </span>
                    </div>
                    <div className="text-xs text-grayscale-1200">
                      <b>{r.signal}</b>{r.why ? ` — ${r.why}` : ''}{r.origin_label ? ` · ${r.origin_label}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      )}
    </div>
  );
};

export default DeliveryTrace;
